import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tray, Plus, CircleNotch, CheckCircle, Sparkle, DownloadSimple, Copy, ChatText, PaperPlaneRight, WarningCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full text-xs border border-slate-300 px-2.5 py-2";
const STATUS = {
  analyzing: ["AI Analyzing", "#1D4ED8"], in_review: ["In Review", "#B45309"],
  changes_requested: ["Changes Requested", "#BE185D"], approved: ["Approved", "#047857"],
  delivered: ["Delivered", "#0F766E"], failed: ["Failed", "#B91C1C"],
};
const ROLES = ["Market Access", "Medical Affairs", "HEOR", "Statistics", "Regulatory"];

function NewQueryDialog({ productId, onClose, onCreated }) {
  const [countries, setCountries] = useState([]);
  const [f, setF] = useState({ country_id: "", question: "", requester: "", requester_role: "Market Access", priority: "normal" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/intelligence/${productId}`).then((r) => setCountries(r.data.countries || [])).catch(console.error);
  }, [productId]);
  const submit = async () => {
    setBusy(true);
    try {
      const c = countries.find((x) => x.id === f.country_id);
      await axios.post(`${API}/queries/${productId}`, { ...f, country_name: c?.name || f.country_id });
      toast.success("Query logged — AI is analysing intent, prior answers and evidence");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create query");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-query-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-sm">New Affiliate Query</DialogTitle></DialogHeader>
        <p className="text-[11px] text-slate-500 -mt-1">The AI will classify the question, check whether it was answered before, retrieve supporting evidence and draft three response formats for review.</p>
        <div className="grid grid-cols-2 gap-2">
          <select data-testid="query-country-select" className={inputCls} value={f.country_id} onChange={(e) => setF({ ...f, country_id: e.target.value })}>
            <option value="">Requesting market *</option>
            {countries.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className={inputCls} value={f.requester_role} onChange={(e) => setF({ ...f, requester_role: e.target.value })}>
            {ROLES.map((r) => <option key={r}>{r}</option>)}
          </select>
          <input className={inputCls} placeholder="Requester (optional)" value={f.requester} onChange={(e) => setF({ ...f, requester: e.target.value })} />
          <select className={inputCls} value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })}>
            <option value="normal">Normal priority</option><option value="high">High priority</option><option value="urgent">Urgent</option>
          </select>
        </div>
        <textarea data-testid="query-question-input" className={`${inputCls} min-h-[120px]`} placeholder='Paste the affiliate question, e.g. "G-BA requests subgroup analyses for patients over 75…"'
          value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} />
        <button data-testid="query-submit-btn" disabled={busy || !f.country_id || f.question.trim().length < 15} onClick={submit}
          className="w-full bg-slate-900 text-white text-xs font-semibold py-2.5 hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2">
          {busy ? <><CircleNotch size={14} className="animate-spin" /> Submitting…</> : "Submit Query"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

function QueryDetail({ q, onChanged }) {
  const [tab, setTab] = useState("affiliate_response");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [comment, setComment] = useState("");
  const [role, setRole] = useState("Market Access");
  const cur = (q.versions || [])[q.versions?.length - 1] || {};
  const a = q.analysis || {};

  const ACT_MSG = { approve: "Query approved", request_changes: "Changes requested", deliver: "Response marked delivered", retry: "Analysis restarted" };
  const act = (action) => axios.post(`${API}/queries/detail/${q.id}/status?action=${action}`)
    .then(() => { toast.success(ACT_MSG[action] || "Updated"); onChanged(); })
    .catch((e) => toast.error(e.response?.data?.detail || "Action failed"));
  const saveEdit = () => axios.put(`${API}/queries/detail/${q.id}/response`, {
    scientific_response: draft.scientific_response ?? cur.scientific_response,
    affiliate_response: draft.affiliate_response ?? cur.affiliate_response,
    executive_summary: draft.executive_summary ?? cur.executive_summary,
  }).then(() => { toast.success(`Saved as version ${(q.versions?.length || 0) + 1}`); setEditing(false); setDraft({}); onChanged(); })
    .catch((e) => toast.error(e.response?.data?.detail || "Save failed"));
  const addComment = () => axios.post(`${API}/queries/detail/${q.id}/comments`, { text: comment, role })
    .then(() => { setComment(""); onChanged(); }).catch(() => toast.error("Comment failed"));
  const download = () => {
    const html = `<html><body><h2>${q.country_name} — Affiliate Response Package</h2><p><b>Question:</b> ${q.question}</p><h3>Affiliate Response</h3><p>${(cur.affiliate_response || "").replace(/\n/g, "<br/>")}</p><h3>Scientific Response</h3><p>${(cur.scientific_response || "").replace(/\n/g, "<br/>")}</p><h3>Executive Summary</h3><p>${(cur.executive_summary || "").replace(/\n/g, "<br/>")}</p><h3>References</h3><ul>${(a.references || []).map((r) => `<li>${r}</li>`).join("")}</ul></body></html>`;
    const blob = new Blob([html], { type: "application/msword" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `response-${q.country_name}-${q.id.slice(0, 6)}.doc`;
    link.click();
  };

  const TABS = [["affiliate_response", "Affiliate-Ready"], ["scientific_response", "Scientific"], ["executive_summary", "Executive Summary"]];
  return (
    <div data-testid={`query-detail-${q.id}`} className="border-t border-slate-100 bg-slate-50/40 px-5 py-4 space-y-4">
      {q.status === "failed" && (
        <div className="flex items-center gap-2 text-xs text-red-700"><WarningCircle size={14} /> {q.error}
          <button data-testid="query-retry-btn" onClick={() => act("retry")} className="underline font-semibold">Retry analysis</button>
        </div>
      )}
      {a.classification && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-3">
          <div>
            <p className="overline-label text-slate-400">AI understanding</p>
            <p className="text-xs text-slate-700 mt-1"><span className="font-semibold">Intent:</span> {a.intent}</p>
            <p className="text-[11px] text-slate-500 mt-1">Evidence confidence: <span className="font-mono-data font-bold">{Math.round((a.confidence || 0) * 100)}%</span></p>
            {a.already_answered?.answered_before && (
              <p data-testid="already-answered-banner" className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 mt-2">
                ✓ Substantially answered before (confidence {Math.round(a.already_answered.confidence * 100)}%) — prior response reused below.
              </p>
            )}
            {a.similar_previous?.length > 0 && (
              <><p className="overline-label text-slate-400 mt-3">Similar previous questions</p>
                {a.similar_previous.map((s, i) => <p key={i} className="text-[11px] text-slate-600 mt-1">• {s.reason}</p>)}</>
            )}
          </div>
          <div>
            <p className="overline-label text-slate-400">Evidence retrieved</p>
            <p className="text-[11px] text-slate-600 mt-1">{(a.evidence_used || []).join(" · ") || "—"}</p>
            {a.evidence_gaps?.length > 0 && <>
              <p className="overline-label text-slate-400 mt-2">Evidence gaps flagged</p>
              {a.evidence_gaps.map((g, i) => <p key={i} className="text-[11px] text-amber-700 mt-0.5">⚠ {g}</p>)}
            </>}
            {a.references?.length > 0 && <>
              <p className="overline-label text-slate-400 mt-2">Suggested references</p>
              {a.references.map((r, i) => <p key={i} className="text-[11px] text-slate-500 mt-0.5">[{i + 1}] {r}</p>)}
            </>}
          </div>
        </div>
      )}
      {cur.affiliate_response !== undefined && (
        <div className="bg-white border border-slate-200">
          <div className="flex items-center border-b border-slate-200">
            {TABS.map(([k, label]) => (
              <button key={k} data-testid={`draft-tab-${k}`} onClick={() => setTab(k)}
                className={`text-[11px] font-semibold px-4 py-2.5 ${tab === k ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"}`}>{label}</button>
            ))}
            <span className="ml-auto text-[10px] text-slate-400 pr-3">v{cur.v} · {cur.edited_by}</span>
          </div>
          {editing ? (
            <textarea data-testid="draft-editor" className="w-full text-xs p-4 min-h-[180px] focus:outline-none"
              value={draft[tab] ?? cur[tab]} onChange={(e) => setDraft({ ...draft, [tab]: e.target.value })} />
          ) : (
            <p data-testid="draft-content" className="text-xs text-slate-700 leading-relaxed p-4 whitespace-pre-wrap">{cur[tab]}</p>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {["in_review", "changes_requested"].includes(q.status) && <>
          {editing ? (
            <button data-testid="save-edit-btn" onClick={saveEdit} className="bg-slate-900 text-white text-[11px] font-semibold px-4 py-2">Save as new version</button>
          ) : (
            <button data-testid="edit-response-btn" onClick={() => setEditing(true)} className="border border-slate-300 text-[11px] font-semibold px-4 py-2 hover:bg-slate-50">Edit response</button>
          )}
          <button data-testid="approve-btn" onClick={() => act("approve")} className="bg-emerald-700 text-white text-[11px] font-semibold px-4 py-2 hover:bg-emerald-800 flex items-center gap-1.5"><CheckCircle size={13} /> Approve</button>
          {q.status === "in_review" && <button data-testid="request-changes-btn" onClick={() => act("request_changes")} className="text-pink-700 border border-pink-200 text-[11px] font-semibold px-4 py-2 hover:bg-pink-50">Request changes</button>}
        </>}
        {q.status === "approved" && <button data-testid="deliver-btn" onClick={() => act("deliver")} className="bg-slate-900 text-white text-[11px] font-semibold px-4 py-2 flex items-center gap-1.5"><PaperPlaneRight size={12} /> Mark delivered</button>}
        {["approved", "delivered"].includes(q.status) && <>
          <button data-testid="download-package-btn" onClick={download} className="border border-slate-300 text-[11px] font-semibold px-4 py-2 hover:bg-slate-50 flex items-center gap-1.5"><DownloadSimple size={12} /> Download package</button>
          <button data-testid="copy-response-btn" onClick={() => navigator.clipboard.writeText(cur.affiliate_response || "").then(() => toast.success("Affiliate response copied")).catch(() => toast.error("Clipboard unavailable — select and copy manually"))}
            className="border border-slate-300 text-[11px] font-semibold px-4 py-2 hover:bg-slate-50 flex items-center gap-1.5"><Copy size={12} /> Copy response</button>
        </>}
      </div>
      <div>
        <p className="overline-label text-slate-400 flex items-center gap-1"><ChatText size={11} /> Review comments ({(q.comments || []).length})</p>
        {(q.comments || []).map((c, i) => (
          <p key={i} className="text-[11px] text-slate-600 mt-1.5"><span className="font-semibold">{c.author}</span> <span className="text-slate-400">({c.role})</span>: {c.text}</p>
        ))}
        <div className="flex gap-2 mt-2">
          <select className="text-[11px] border border-slate-300 px-2" value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLES.map((r) => <option key={r}>{r}</option>)}
          </select>
          <input data-testid="comment-input" className={`${inputCls} flex-1`} placeholder="Add a review comment…" value={comment} onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && comment.trim() && addComment()} />
          <button data-testid="comment-submit-btn" disabled={!comment.trim()} onClick={addComment} className="bg-slate-900 text-white text-[11px] font-semibold px-4 disabled:opacity-50">Post</button>
        </div>
      </div>
    </div>
  );
}

function LearningTab({ productId }) {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/queries/analytics/${productId}`).then((r) => setD(r.data)).catch(console.error); }, [productId]);
  if (!d) return <div className="py-16 text-center text-slate-400 text-sm">Loading analytics…</div>;
  const Panel = ({ title, children }) => (
    <div className="bg-white border border-slate-200"><p className="overline-label text-slate-400 px-4 pt-3">{title}</p><div className="px-4 py-3">{children}</div></div>
  );
  return (
    <div data-testid="learning-dashboard" className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 bg-white border border-slate-200 divide-x divide-slate-200">
        {[["Total Queries", d.totals.total], ["Awaiting Review", d.totals.in_review],
          ["Avg Response Time", d.totals.avg_response_hours != null ? `${d.totals.avg_response_hours}h` : "—"],
          ["Knowledge Reuse Rate", `${d.totals.reuse_rate}%`]].map(([l, v]) => (
          <div key={l} className="px-4 py-3"><p className="overline-label text-slate-400">{l}</p><p className="font-heading font-extrabold text-xl mt-0.5">{v}</p></div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Most common question types">
          {d.by_category.map((c) => <p key={c.category} className="text-xs text-slate-600 py-0.5 flex justify-between"><span>{c.category}</span><span className="font-mono-data font-bold">{c.count}</span></p>)}
          {d.by_category.length === 0 && <p className="text-xs text-slate-400">No data yet.</p>}
        </Panel>
        <Panel title="Workload by country">
          {d.by_country.map((c) => <p key={c.country} className="text-xs text-slate-600 py-0.5 flex justify-between"><span>{c.country}</span><span className="font-mono-data font-bold">{c.count}</span></p>)}
          {d.by_country.length === 0 && <p className="text-xs text-slate-400">No data yet.</p>}
        </Panel>
        <Panel title="Most referenced evidence">
          {d.top_evidence.map((e) => <p key={e.asset} className="text-xs text-slate-600 py-0.5 flex justify-between gap-3"><span className="truncate">{e.asset}</span><span className="font-mono-data font-bold shrink-0">{e.count}</span></p>)}
          {d.top_evidence.length === 0 && <p className="text-xs text-slate-400">No data yet.</p>}
        </Panel>
        <Panel title="Recurring evidence gaps">
          {d.recurring_gaps.map((g, i) => <p key={i} className="text-xs text-amber-700 py-0.5">⚠ {g.gap} <span className="text-slate-400">×{g.count}</span></p>)}
          {d.recurring_gaps.length === 0 && <p className="text-xs text-slate-400">None flagged yet.</p>}
        </Panel>
      </div>
      {d.recommendations.length > 0 && (
        <div data-testid="learning-recommendations" className="bg-slate-900 text-white p-5">
          <p className="overline-label text-emerald-400">Organisational learning — recommended actions</p>
          {d.recommendations.map((r, i) => (
            <p key={i} className="text-xs text-slate-300 leading-relaxed mt-2"><span className="font-semibold text-white">{r.theme}</span> asked {r.occurrences}× → {r.recommendation}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export default function QueriesPage() {
  const { productId, product } = useProduct();
  const [searchParams] = useSearchParams();
  const urlTab = searchParams.get("tab") === "learning" ? "learning" : "inbox";
  const [queries, setQueries] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [tab, setTab] = useState(urlTab);
  useEffect(() => { setTab(urlTab); }, [urlTab]);
  const [dialog, setDialog] = useState(false);
  const [filters, setFilters] = useState({ status: "" });
  const pollRef = useRef(null);

  const load = useCallback(() => {
    const params = {};
    if (filters.status) params.status = filters.status;
    return axios.get(`${API}/queries/${productId}`, { params }).then((r) => { setQueries(r.data); return r.data; });
  }, [productId, filters]);

  useEffect(() => { setQueries(null); setOpenId(null); if (productId) load().catch(console.error); }, [productId, load]);
  useEffect(() => {
    clearInterval(pollRef.current);
    if (queries?.some((q) => q.status === "analyzing")) pollRef.current = setInterval(() => load().catch(() => {}), 5000);
    return () => clearInterval(pollRef.current);
  }, [queries, load]);

  return (
    <PageShell pageId="queries" overline={`MAI Knowledge Hub™ · ${product?.name || ""}`} title="Incoming Queries"
      actions={
        <button data-testid="new-query-btn" onClick={() => setDialog(true)}
          className="bg-slate-900 text-white text-xs font-semibold px-4 py-2.5 hover:bg-slate-800 flex items-center gap-2">
          <Plus size={14} weight="bold" /> New Affiliate Query
        </button>
      }>
      <div className="flex items-center gap-2">
        {[["inbox", "Query Inbox"], ["learning", "Organisational Learning"]].map(([k, label]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
            className={`text-xs font-semibold px-4 py-2 border ${tab === k ? "bg-slate-900 text-white border-slate-900" : "text-slate-500 border-slate-200 hover:text-slate-900"}`}>{label}</button>
        ))}
        {tab === "inbox" && (
          <select data-testid="filter-status" className="ml-auto text-[11px] border border-slate-200 px-2 py-1.5 bg-white text-slate-600"
            value={filters.status} onChange={(e) => setFilters({ status: e.target.value })}>
            <option value="">All statuses</option>
            {Object.entries(STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
          </select>
        )}
      </div>

      {tab === "learning" ? <LearningTab productId={productId} /> : !queries ? (
        <div className="py-24 text-center text-slate-400 text-sm">Loading inbox…</div>
      ) : queries.length === 0 ? (
        <div data-testid="queries-empty" className="bg-white border border-slate-200 py-16 text-center">
          <Tray size={32} className="text-slate-300 mx-auto" />
          <p className="font-heading font-bold text-sm text-slate-700 mt-3">Every affiliate question becomes institutional knowledge.</p>
          <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">Log a question from any market — the AI classifies it, checks whether it was answered before, retrieves your evidence and drafts the response for review.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {queries.map((q) => {
            const [sl, sc] = STATUS[q.status] || STATUS.in_review;
            const cat = q.analysis?.classification;
            return (
              <div key={q.id} data-testid={`query-card-${q.id}`} className="bg-white border border-slate-200">
                <button onClick={() => setOpenId(openId === q.id ? null : q.id)} className="w-full px-5 py-3.5 text-left hover:bg-slate-50/60">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 text-white" style={{ background: sc }}>
                      {q.status === "analyzing" ? <span className="inline-flex items-center gap-1"><CircleNotch size={9} className="animate-spin" /> {sl}</span> : sl}
                    </span>
                    {cat && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border border-slate-300 text-slate-500">{cat.replace(/_/g, " ")}</span>}
                    {q.priority !== "normal" && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-red-600 text-white">{q.priority}</span>}
                    {q.analysis?.already_answered?.answered_before && <span className="text-[9px] uppercase tracking-widest font-bold text-emerald-700 flex items-center gap-1"><Sparkle size={10} weight="fill" /> Answered before</span>}
                    <span className="font-mono-data text-[10px] text-slate-400 ml-auto">{q.country_name} · {new Date(q.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-xs font-semibold text-slate-800 mt-1.5 leading-relaxed">{q.question}</p>
                </button>
                {openId === q.id && <QueryDetail q={q} onChanged={load} />}
              </div>
            );
          })}
        </div>
      )}
      {dialog && <NewQueryDialog productId={productId} onClose={() => setDialog(false)} onCreated={() => { setDialog(false); load(); }} />}
    </PageShell>
  );
}
